import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  computed,
  effect,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe, TitleCasePipe, NgClass } from '@angular/common';
import { NavbarComponent } from '../../../../shell/navbar/navbar.component';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../../../../environments/environment';
import { VirtualAssistantService } from 'src/shared/services/api/virtual-assistant.service';
import { getCountries } from 'src/service/utility/country.utils';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs/operators';
import {
  buildEducationalContentViewModel,
  EducationalContentViewModel,
  hasEducationalViewContent,
  parseEducationalContentResponse,
} from './educational-content-view-model';
import { EducationalContentViewComponent } from './educational-content-view.component';
import { AssistantConversationStateService } from '../orchestrator/assistant-conversation-state.service';
import type { WorkflowSlotSnapshot } from '../models/conversation-state.model';

/** Structured payload from `generate-educational-content` (and compatible history items). */
export interface EducationalGeneratedContent {
  contentType?: string;
  title?: string;
  contentLanguage?: string;
  lyrics?: string[];
  learningOutcomes?: unknown[];
  materials?: unknown[];
  activityInstructions?: unknown[];
  pedagogicalNotes?: unknown[];
  culturalElements?: unknown[];
  languageIntegration?: {
    localWords?: Array<{ word?: string; translation?: string }>;
    multilingualSupport?: string;
    [key: string]: unknown;
  };
  educationalStandardsAlignment?: {
    framework?: string;
    alignmentDetails?: unknown[];
    [key: string]: unknown;
  };
  safetyNotes?: unknown[];
  differentiation?: unknown[];
  suggestions?: unknown[];
  [key: string]: unknown;
}

@Component({
  selector: 'app-knowledge-setup',
  standalone: true,
  imports: [
    FormsModule,
    NavbarComponent,
    TranslateModule,
    DatePipe,
    TitleCasePipe,
    NgClass,
    NgbDropdownModule,
    EducationalContentViewComponent,
  ],
  templateUrl: './knowledge-setup.component.html',
  styleUrl: './knowledge-setup.component.css',
})
export class KnowledgeSetupComponent implements OnInit, OnChanges {
  private virtualAssistantService = inject(VirtualAssistantService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly convState = inject(AssistantConversationStateService);

  private lastRemoteEpoch = -1;
  private pendingRemoteDomain: { id: number; name: string } | null = null;
  private pendingRemoteQuestion: { id: number; content: string } | null = null;
  /** Mirrors embedMode for reactive effects (inputs are not signals). */
  readonly isEmbedForConv = signal(false);

  @Input() embedMode = false;

  /** When set (e.g. from assistant embed), overrides `history.state.artifact`. */
  @Input({ alias: 'artifact' }) artifactKey: string | null = null;

  @Output() readonly back = new EventEmitter<void>();

  /** Resolved artifact id for API + i18n keys */
  private resolvedArtifact = 'activities';

  get artifact(): string {
    return this.resolvedArtifact;
  }

  navbarDisplay: string = 'none';
  genratingResponse: boolean = false;
  isLoadingDomains: boolean = false;
  isLoadingQuestions: boolean = false;

  // History related
  showHistory: boolean = false;
  isLoadingHistory: boolean = false;
  historyList: any[] = [];
  historyPagination: any = null;

  // Check if FastTrack environment
  isFtkEnvironment = computed(() => environment.envName === 'fastrack');

  // Data
  domains: any[] = [];
  questions: any[] = [];
  pedagogies = ['Montessori', 'Play-based', 'Storytelling', 'Worksheet-based'];
  levels = [
    'Playgroup (2-3 yrs)',
    'Nursery (3-4 yrs)',
    'LKG (4-5 yrs)',
    'UKG (5-6 yrs)',
    'Grade 1 (6-7 yrs)',
    'Grade 2 (7-8 yrs)',
  ];
  countries: any[] = getCountries();

  // Selections
  selectedDomain: any = null;
  selectedQuestion: any = null;
  selectedPedagogy: string | null = null;
  selectedLevel: string = 'LKG (4-5 yrs)';
  selectedCountry: string = 'Global';

  // Computed
  finalPrompt: string = '';
  promptResponse: string | undefined = undefined;
  finalFormat: any;
  /** New API: structured content under `response.data` / `response.data.data`. */
  generatedContent: EducationalGeneratedContent | null = null;
  /** Normalized view model for dynamic educational content UI. */
  generatedView: EducationalContentViewModel | null = null;
  teacherId: number | null = null;
  schoolId: number | null = null;
  classId: number | null = null;

  private readonly _remoteSlotSync = effect(() => {
    const embed = this.isEmbedForConv();
    const epoch = this.convState.remoteApplyEpoch();
    if (!embed) return;
    if (epoch === this.lastRemoteEpoch) return;
    this.lastRemoteEpoch = epoch;
    this.applyRemoteSnapshot(this.convState.snapshot());
    this.cdr.markForCheck();
  });

  constructor() {
    if (typeof window !== 'undefined') {
      const storedLevel = localStorage.getItem('class');
      const storedUser = localStorage.getItem('user');

      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          this.teacherId = Number(user.teacher_id) || null;
          this.schoolId = Number(user.school_id) || null;
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          this.teacherId = null;
          this.schoolId = null;
        }
      }

      if (storedLevel) {
        const matchedLevel = this.levels.find(l =>
          l.toLowerCase().includes(storedLevel.toLowerCase())
        );
        if (matchedLevel) {
          this.selectedLevel = matchedLevel;
        } else {
          this.selectedLevel = storedLevel;
          if (!this.levels.includes(storedLevel)) {
            this.levels.push(storedLevel);
          }
        }
      }
    }
  }

  ngOnInit(): void {
    this.isEmbedForConv.set(this.embedMode);
    this.syncResolvedArtifact(true);
    this.loadDomains();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['embedMode']) {
      this.isEmbedForConv.set(this.embedMode);
      if (!this.embedMode) {
        this.lastRemoteEpoch = -1;
      }
    }
    if (changes['artifactKey'] || changes['embedMode']) {
      this.syncResolvedArtifact(false);
    }
  }

  onEmbedBack(): void {
    this.back.emit();
  }

  /** NgbDropdown selections in embed mode mirror native `<select>` behavior */
  pickEmbedDomain(domain: any | null): void {
    this.selectedDomain = domain;
    this.onDomainChange();
    this.pushFormToConversationState();
  }

  pickEmbedQuestion(q: any | null): void {
    this.selectedQuestion = q;
    this.pushFormToConversationState();
  }

  pickEmbedPedagogy(value: string | null): void {
    this.selectedPedagogy = value;
    this.pushFormToConversationState();
  }

  pickEmbedCountry(value: string): void {
    this.selectedCountry = value;
    this.pushFormToConversationState();
  }

  pickEmbedLevel(value: string): void {
    this.selectedLevel = value;
    this.pushFormToConversationState();
  }

  embedSelectedCountryLabel(): string {
    return (
      this.countries.find(c => c.value === this.selectedCountry)?.label ??
      this.selectedCountry
    );
  }

  private readHistoryArtifact(): string | null {
    if (typeof history === 'undefined' || !history.state?.artifact) {
      return null;
    }
    return String(history.state.artifact);
  }

  /**
   * Embeds always prefer `artifactKey` when provided; full page uses history.state when input is unset.
   */
  private syncResolvedArtifact(initial: boolean): void {
    const fromInput = this.artifactKey;
    const fromHistory = this.readHistoryArtifact();
    let next = 'activities';
    if (this.embedMode) {
      next = fromInput || 'activities';
    } else {
      next = fromInput || fromHistory || 'activities';
    }
    if (next !== this.resolvedArtifact) {
      this.resolvedArtifact = next;
      this.resetGenerationState();
    } else if (initial) {
      this.resolvedArtifact = next;
    }
  }

  private resetGenerationState(): void {
    this.finalFormat = undefined;
    this.generatedContent = null;
    this.generatedView = null;
    this.promptResponse = undefined;
    this.showHistory = false;
    this.genratingResponse = false;
    this.selectedDomain = null;
    this.selectedQuestion = null;
    this.questions = [];
    this.historyList = [];
    this.isLoadingHistory = false;
  }

  private applyRemoteSnapshot(snap: WorkflowSlotSnapshot): void {
    if (snap.pedagogy) {
      this.selectedPedagogy = snap.pedagogy;
    }
    if (snap.standardLevel) {
      this.selectedLevel = snap.standardLevel;
    }
    if (snap.countryRegion != null && snap.countryRegion !== '') {
      this.selectedCountry = snap.countryRegion;
    }
    if (snap.topicDomain) {
      const d = this.domains.find((x: any) => x.id === snap.topicDomain!.id);
      if (d) {
        this.pendingRemoteDomain = null;
        if (this.selectedDomain?.id !== d.id) {
          this.selectedDomain = d;
          this.onDomainChange();
        }
      } else {
        this.pendingRemoteDomain = snap.topicDomain;
      }
    }
    if (snap.learningFocus) {
      const q = this.questions.find((x: any) => x.id === snap.learningFocus!.id);
      if (q) {
        this.pendingRemoteQuestion = null;
        this.selectedQuestion = q;
      } else {
        this.pendingRemoteQuestion = snap.learningFocus;
      }
    }
  }

  private pushFormToConversationState(): void {
    if (!this.embedMode) return;
    const patch: Partial<WorkflowSlotSnapshot> = {};
    if (this.selectedDomain) {
      patch.topicDomain = {
        id: this.selectedDomain.id,
        name: this.selectedDomain.name,
      };
    }
    if (this.selectedQuestion) {
      patch.learningFocus = {
        id: this.selectedQuestion.id,
        content: this.selectedQuestion.content,
      };
    }
    if (this.selectedPedagogy) {
      patch.pedagogy = this.selectedPedagogy;
    }
    if (this.selectedLevel) {
      patch.standardLevel = this.selectedLevel;
    }
    if (this.selectedCountry) {
      patch.countryRegion = this.selectedCountry;
    }
    if (Object.keys(patch).length > 0) {
      this.convState.syncFromForm(patch);
    }
  }

  loadDomains() {
    this.isLoadingDomains = true;
    const language =
      typeof window !== 'undefined'
        ? localStorage.getItem('targetedLang') || 'en'
        : 'en';

    this.virtualAssistantService.getDomains(14, language).subscribe({
      next: (response: any) => {
        if (response.data) {
          this.domains = response.data;
          if (this.pendingRemoteDomain) {
            const d = this.domains.find((x: any) => x.id === this.pendingRemoteDomain!.id);
            if (d) {
              this.pendingRemoteDomain = null;
              this.selectedDomain = d;
              this.onDomainChange();
            }
          }
        }
        this.isLoadingDomains = false;
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Error loading domains:', error);
        this.isLoadingDomains = false;
      },
    });
  }

  onDomainChange() {
    this.selectedQuestion = null;
    this.questions = [];

    if (!this.selectedDomain) {
      this.pushFormToConversationState();
      return;
    }

    this.isLoadingQuestions = true;
    const language =
      typeof window !== 'undefined'
        ? localStorage.getItem('targetedLang') || 'en'
        : 'en';

    this.virtualAssistantService
      .getQuestionsBySection(this.selectedDomain.id, language)
      .subscribe({
        next: (response: any) => {
          if (response.data) {
            this.questions = response.data;
          }
          this.isLoadingQuestions = false;
          if (this.pendingRemoteQuestion) {
            const q = this.questions.find((x: any) => x.id === this.pendingRemoteQuestion!.id);
            if (q) {
              this.pendingRemoteQuestion = null;
              this.selectedQuestion = q;
            }
          }
          this.pushFormToConversationState();
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading questions:', error);
          this.isLoadingQuestions = false;
        },
      });
  }

  isFormValid() {
    return (
      this.selectedDomain &&
      this.selectedQuestion &&
      this.selectedPedagogy &&
      this.selectedLevel
    );
  }

  /** Normalize list items from API (strings or small objects) for display. */
  itemLabel(item: unknown): string {
    if (item == null) return '';
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
      const o = item as Record<string, unknown>;
      for (const k of ['text', 'title', 'description', 'outcome', 'note', 'step', 'detail']) {
        const v = o[k];
        if (typeof v === 'string') return v;
      }
    }
    try {
      return JSON.stringify(item);
    } catch {
      return String(item);
    }
  }

  hasList(values: readonly unknown[] | undefined | null): boolean {
    return Array.isArray(values) && values.length > 0;
  }

  private looksLikeGeneratedContent(obj: any): boolean {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
    if (Array.isArray(obj.suggestions)) return false;

    const s = (k: string) =>
      typeof obj[k] === 'string' && String(obj[k]).trim().length > 0;
    const content =
      obj.content && typeof obj.content === 'object' && !Array.isArray(obj.content)
        ? obj.content
        : null;
    const hasContentBlock =
      (content != null && Object.keys(content).length > 0) ||
      (Array.isArray(obj.content) && obj.content.length > 0);

    return (
      obj.title != null ||
      s('description') ||
      s('titleTranslation') ||
      obj.contentType != null ||
      (Array.isArray(obj.lyrics) && obj.lyrics.length > 0) ||
      (Array.isArray(obj.learningOutcomes) && obj.learningOutcomes.length > 0) ||
      (typeof obj.learningOutcome === 'string' && obj.learningOutcome.trim()) ||
      Array.isArray(obj.learningOutcome) ||
      (Array.isArray(obj.materials) && obj.materials.length > 0) ||
      (Array.isArray(obj.activityInstructions) && obj.activityInstructions.length > 0) ||
      (Array.isArray(obj.pedagogicalNotes) && obj.pedagogicalNotes.length > 0) ||
      obj.pedagogicalApproach != null ||
      (Array.isArray(obj.safetyNotes) && obj.safetyNotes.length > 0) ||
      (Array.isArray(obj.safetyConsiderations) && obj.safetyConsiderations.length > 0) ||
      obj.inclusiveDesign != null ||
      (Array.isArray(obj.differentiation) && obj.differentiation.length > 0) ||
      (obj.languageIntegration && typeof obj.languageIntegration === 'object') ||
      (obj.educationalStandardsAlignment &&
        typeof obj.educationalStandardsAlignment === 'object') ||
      (Array.isArray(obj.culturalElements) && obj.culturalElements.length > 0) ||
      hasContentBlock
    );
  }

  /**
   * Supports: `{ data: { ...fields } }`, `{ data: { data: { ...fields } } }`, legacy `{ suggestions: [...] }`.
   */
  private extractGeneratedPayload(response: any): any | null {
    if (response == null) return null;
    const envelope = response?.data;
    const nested =
      envelope && typeof envelope === 'object' && !Array.isArray(envelope)
        ? (envelope as any).data
        : undefined;

    const tryLegacy = (node: any) =>
      node &&
      typeof node === 'object' &&
      Array.isArray(node.suggestions) &&
      node.suggestions.length > 0
        ? node
        : null;
    const tryNew = (node: any) =>
      node && typeof node === 'object' && this.looksLikeGeneratedContent(node) ? node : null;

    return (
      tryLegacy(response) ||
      tryLegacy(envelope) ||
      tryLegacy(nested) ||
      tryNew(nested) ||
      tryNew(envelope) ||
      tryNew(response)
    );
  }

  private assignEducationalResponse(response: any): void {
    this.generatedContent = null;
    this.generatedView = null;
    this.finalFormat = null;

    const parsed = parseEducationalContentResponse(response, '', {
      artifact: this.artifact,
    });
    if (parsed.view) {
      this.generatedView = parsed.view;
      return;
    }

    const payload = this.extractGeneratedPayload(response);
    if (!payload) return;
    if (Array.isArray(payload.suggestions) && payload.suggestions.length > 0) {
      this.finalFormat = payload;
      return;
    }
    const normalized = this.normalizeStructuredPayload(
      payload as EducationalGeneratedContent
    );
    const vm = buildEducationalContentViewModel(normalized);
    if (vm && hasEducationalViewContent(vm)) {
      this.generatedContent = normalized;
      this.generatedView = vm;
    }
  }

  /** Coerce occasional string `lyrics` into string[]. */
  private normalizeStructuredPayload(
    c: EducationalGeneratedContent
  ): EducationalGeneratedContent {
    const ly = c.lyrics as unknown;
    if (typeof ly === 'string') {
      return { ...c, lyrics: ly.split('\n').filter(line => line.trim().length > 0) };
    }
    return c;
  }

  generatePrompt() {
    this.genratingResponse = true;
    this.generatedContent = null;
    this.generatedView = null;
    this.finalFormat = null;

    const language =
      typeof window !== 'undefined'
        ? localStorage.getItem('targetedLang') || 'en'
        : 'en';

    const payload: any = {
      teacherId: this.teacherId,
      schoolId: this.schoolId,
      domainName: this.selectedDomain.name,
      questionContent: this.selectedQuestion.content,
      pedagogy: this.selectedPedagogy,
      level: this.selectedLevel,
      artifact: this.artifact,
      numberOfSuggestions: 1,
      country: this.selectedCountry,
      language: language,
      includeLocalContext: this.selectedCountry !== 'Global',
    };

    this.virtualAssistantService
      .generateEducationalContent(payload)
      .pipe(
        finalize(() => {
          this.genratingResponse = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (response: any) => {
          try {
            this.assignEducationalResponse(response);

            if (this.generatedView || this.finalFormat) {
              this.promptResponse = JSON.stringify(
                this.generatedContent ?? this.finalFormat
              );
              setTimeout(() => {
                const resultsPanel = document.querySelector('.results-panel');
                if (resultsPanel && window.innerWidth < 768) {
                  resultsPanel.scrollIntoView({ behavior: 'smooth' });
                }
              }, 100);
            } else {
              console.error('Invalid response format:', response);
              this.promptResponse = 'Failed to generate content.';
            }
          } catch (e) {
            console.error('Error processing generate response:', e);
            this.promptResponse = 'Failed to generate content.';
          }
        },
        error: (error: any) => {
          console.error('Backend API error:', error);
          this.promptResponse = 'Failed to get response from backend.';
        },
      });
  }

  formatSteps(raw: string): string {
    return raw.replace(/(^|\s)(\d+\.\s)/g, '$1\n$2');
  }

  toggleHistory() {
    this.showHistory = !this.showHistory;
    if (this.showHistory && this.historyList.length === 0) {
      this.loadHistory();
    }
    this.cdr.markForCheck();
  }

  /**
   * Teacher history API envelope (tolerant of optional nesting).
   * Parent Voice Assistant uses OnPush — markForCheck after mutating state from async callbacks.
   */
  private extractTeacherHistoryPayload(response: unknown): {
    history: unknown[];
    pagination: unknown;
  } {
    const r = response as Record<string, unknown> | null;
    if (!r) {
      return { history: [], pagination: null };
    }
    const envelope = r['data'];
    if (envelope && typeof envelope === 'object' && !Array.isArray(envelope)) {
      const d = envelope as Record<string, unknown>;
      const inner = d['data'];
      const bucket =
        inner && typeof inner === 'object' && !Array.isArray(inner)
          ? (inner as Record<string, unknown>)
          : d;
      const history = bucket['history'];
      if (Array.isArray(history)) {
        return {
          history,
          pagination: bucket['pagination'] ?? null,
        };
      }
    }
    const topHistory = r['history'];
    if (Array.isArray(topHistory)) {
      return {
        history: topHistory,
        pagination: r['pagination'] ?? null,
      };
    }
    return { history: [], pagination: null };
  }

  loadHistory(limit: number = 10, offset: number = 0) {
    this.isLoadingHistory = true;
    this.cdr.markForCheck();
    this.virtualAssistantService
      .getTeacherHistory(this.artifact, Number(this.teacherId), limit, offset)
      .pipe(
        finalize(() => {
          this.isLoadingHistory = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (response: unknown) => {
          const { history, pagination } = this.extractTeacherHistoryPayload(response);
          this.historyList = history;
          this.historyPagination = pagination;
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          console.error('Error loading history:', error);
          this.historyList = [];
          this.historyPagination = null;
          this.cdr.markForCheck();
        },
      });
  }

  viewHistoryItem(historyId: number) {
    this.genratingResponse = true;
    this.virtualAssistantService
      .getResponseById(historyId)
      .pipe(
        finalize(() => {
          this.genratingResponse = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (response: any) => {
          try {
            this.assignEducationalResponse(response);

            if (this.generatedView || this.finalFormat) {
              this.promptResponse = JSON.stringify(
                this.generatedContent ?? this.finalFormat
              );
              this.showHistory = false;
            }
          } catch (e) {
            console.error('Error processing history response:', e);
          }
        },
        error: (error: any) => {
          console.error('Error loading response:', error);
        },
      });
  }

  closeHistory() {
    this.showHistory = false;
  }

  /** Section visibility: full page hides empty results on small screens; embed always shows stack. */
  resultsSectionDisplayClass(): string {
    if (this.embedMode) {
      return 'flex';
    }
    return this.showHistory ||
      this.genratingResponse ||
      this.finalFormat ||
      this.generatedView
      ? 'flex'
      : 'hidden md:flex';
  }
}
